import { useEffect, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import api, { fmtErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { UserAvatar } from "@/components/Avatar";
import { COUNTRIES, ALL_TIMEZONES, tzOffsetLabel, tzLabel } from "@/lib/timezones";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { Check, ChevronsUpDown } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Camera, Loader2, Lock } from "lucide-react";
import { useTranslation } from "react-i18next";

const COUNTRY_CODES = [
  ["+90", "TR Turkey"], ["+971", "AE UAE"], ["+44", "GB United Kingdom"], ["+1", "US/CA +1"], ["+49", "DE Germany"],
  ["+33", "FR France"], ["+39", "IT Italy"], ["+34", "ES Spain"], ["+31", "NL Netherlands"], ["+32", "BE Belgium"],
  ["+41", "CH Switzerland"], ["+43", "AT Austria"], ["+46", "SE Sweden"], ["+47", "NO Norway"], ["+45", "DK Denmark"],
  ["+358", "FI Finland"], ["+353", "IE Ireland"], ["+351", "PT Portugal"], ["+30", "GR Greece"], ["+48", "PL Poland"],
  ["+420", "CZ Czechia"], ["+36", "HU Hungary"], ["+40", "RO Romania"], ["+7", "RU Russia"], ["+38", "UA Ukraine"],
  ["+966", "SA Saudi Arabia"], ["+974", "QA Qatar"], ["+965", "KW Kuwait"], ["+973", "BH Bahrain"], ["+968", "OM Oman"],
  ["+962", "JO Jordan"], ["+961", "LB Lebanon"], ["+20", "EG Egypt"], ["+27", "ZA South Africa"], ["+91", "IN India"],
  ["+86", "CN China"], ["+81", "JP Japan"], ["+82", "KR South Korea"], ["+65", "SG Singapore"], ["+60", "MY Malaysia"],
  ["+62", "ID Indonesia"], ["+63", "PH Philippines"], ["+66", "TH Thailand"], ["+84", "VN Vietnam"], ["+61", "AU Australia"],
  ["+64", "NZ New Zealand"], ["+55", "BR Brazil"], ["+52", "MX Mexico"], ["+54", "AR Argentina"],
];

export default function Profile() {
  const { user, setUser } = useAuth();
  const { t } = useTranslation();
  const fileRef = useRef(null);
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    name: user?.name || "",
    job_title: user?.job_title || "",
    phone_country_code: user?.phone_country_code || "+971",
    phone_number: user?.phone_number || "",
    country: user?.country || "",
    timezone: user?.timezone || "",
  });

  useEffect(() => {
    if (user) {
      setForm({
        name: user.name || "",
        job_title: user.job_title || "",
        phone_country_code: user.phone_country_code || "+971",
        phone_number: user.phone_number || "",
        country: user.country || "",
        timezone: user.timezone || "",
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional: reseed the form only when the user identity changes, not on every profile field update (would wipe mid-edit input)
  }, [user?.id]);

  useEffect(() => {
    if (!file) {
      setPreview(null);
      return;
    }
    const url = URL.createObjectURL(file);
    setPreview(url);
    return () => URL.revokeObjectURL(url);
  }, [file]);

  const qc = useQueryClient();
  const [tzOpen, setTzOpen] = useState(false);

  const refreshUser = async () => {
    const { data } = await api.get("/auth/me");
    setUser(data);
  };

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/profile", form);
      setUser(data);
      toast.success(t("profile.toastUpdated"));
    } catch (e) {
      toast.error(fmtErr(e));
    } finally {
      setSaving(false);
    }
  };

  const compressImage = (imgFile, maxSize = 512, quality = 0.85) =>
    new Promise((resolve, reject) => {
      const img = new Image();
      const url = URL.createObjectURL(imgFile);
      img.onload = () => {
        const scale = Math.min(1, maxSize / Math.max(img.width, img.height));
        const w = Math.max(1, Math.round(img.width * scale));
        const h = Math.max(1, Math.round(img.height * scale));
        const canvas = document.createElement("canvas");
        canvas.width = w;
        canvas.height = h;
        canvas.getContext("2d").drawImage(img, 0, 0, w, h);
        URL.revokeObjectURL(url);
        canvas.toBlob((b) => (b ? resolve(b) : reject(new Error("Compression failed"))), "image/jpeg", quality);
      };
      img.onerror = () => {
        URL.revokeObjectURL(url);
        // Some formats (e.g. HEIC) can't be decoded by this browser — fall back to the original file if small enough
        if (imgFile.size <= 4.5 * 1024 * 1024) resolve(imgFile);
        else reject(new Error("This image format can't be processed here and is over 5 MB — please use a JPG or PNG photo."));
      };
      img.src = url;
    });

  const uploadAvatar = async () => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      const blob = await compressImage(file);
      fd.append("file", blob, blob === file ? file.name : "avatar.jpg");
      await api.post("/profile/avatar", fd);
      await refreshUser();
      qc.invalidateQueries();
      setFile(null);
      if (fileRef.current) fileRef.current.value = "";
      toast.success(t("profile.toastPhotoUpdated"));
    } catch (e) {
      toast.error(fmtErr(e));
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="max-w-3xl space-y-6" data-testid="profile-page">
      <div>
        <h1 className="font-display text-2xl font-bold tracking-tight">{t("profile.title")}</h1>
        <p className="text-sm text-slate-500">{t("profile.subtitle")}</p>
      </div>

      <Card data-testid="profile-photo-card">
        <CardHeader className="pb-2"><CardTitle className="text-base">{t("profile.photoCard")}</CardTitle></CardHeader>
        <CardContent className="flex flex-wrap items-center gap-5">
          {preview ? (
            <img src={preview} alt="preview" className="h-20 w-20 rounded-full object-cover" data-testid="avatar-preview" />
          ) : (
            <UserAvatar user={user} size="h-20 w-20" textSize="text-2xl" testId="profile-avatar" />
          )}
          <div className="space-y-2">
            <input
              ref={fileRef}
              type="file"
              accept="image/*"
              className="hidden"
              onChange={(e) => setFile(e.target.files[0] || null)}
              data-testid="avatar-file-input"
            />
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={() => fileRef.current?.click()} data-testid="avatar-choose-button">
                <Camera className="mr-2 h-4 w-4" /> {t("profile.choosePhoto")}
              </Button>
              <Button size="sm" onClick={uploadAvatar} disabled={!file || uploading} className="bg-slate-900 hover:bg-slate-800" data-testid="avatar-upload-button">
                {uploading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} {t("common.upload")}
              </Button>
            </div>
            <p className="text-xs text-slate-500">{t("profile.photoHint")}</p>
          </div>
        </CardContent>
      </Card>

      <Card data-testid="profile-details-card">
        <CardHeader className="pb-2"><CardTitle className="text-base">{t("profile.detailsCard")}</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label>{t("profile.fullName")}</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="profile-name-input" />
            </div>
            <div className="space-y-1.5">
              <Label>{t("profile.jobTitle")}</Label>
              <Input value={form.job_title} onChange={(e) => setForm({ ...form, job_title: e.target.value })} placeholder={t("profile.jobTitlePh")} data-testid="profile-job-title-input" />
            </div>
            <div className="space-y-1.5">
              <Label>{t("profile.phone")}</Label>
              <div className="flex gap-2">
                <Select value={form.phone_country_code} onValueChange={(v) => setForm({ ...form, phone_country_code: v })}>
                  <SelectTrigger className="w-36 font-mono" data-testid="profile-country-code-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="max-h-72">
                    {COUNTRY_CODES.map(([code, label]) => (
                      <SelectItem key={label} value={code} data-testid={`country-code-${code.replace("+", "")}`}>
                        <span className="font-mono">{code}</span> <span className="text-slate-500">{label.split(" ").slice(1).join(" ")}</span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Input
                  value={form.phone_number}
                  onChange={(e) => setForm({ ...form, phone_number: e.target.value })}
                  placeholder="555 123 4567"
                  className="font-mono"
                  data-testid="profile-phone-input"
                />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label>{t("profile.country")}</Label>
              <Select
                value={form.country || "none"}
                onValueChange={(v) => setForm({ ...form, country: v === "none" ? "" : v })}
              >
                <SelectTrigger data-testid="profile-country-select"><SelectValue placeholder={t("profile.selectCountry")} /></SelectTrigger>
                <SelectContent className="max-h-72">
                  <SelectItem value="none" data-testid="profile-country-none">{t("profile.notSet")}</SelectItem>
                  {COUNTRIES.map((c) => (
                    <SelectItem key={c.code} value={c.code} data-testid={`profile-country-${c.code}`}>{c.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>{t("profile.tzLabel")}</Label>
              <Popover open={tzOpen} onOpenChange={setTzOpen}>
                <PopoverTrigger asChild>
                  <Button variant="outline" role="combobox" className="w-full justify-between font-normal" data-testid="profile-timezone-select">
                    {form.timezone ? (
                      <span>{tzLabel(form.timezone)} <span className="font-mono text-xs text-slate-400">({tzOffsetLabel(form.timezone)})</span></span>
                    ) : (
                      <span className="text-slate-500">{t("profile.tzSearch")}</span>
                    )}
                    <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
                  <Command>
                    <CommandInput placeholder={t("profile.tzInputPh")} data-testid="profile-tz-search" />
                    <CommandList className="max-h-64">
                      <CommandEmpty>{t("profile.tzEmpty")}</CommandEmpty>
                      <CommandGroup>
                        {form.timezone && (
                          <CommandItem value="not-set" onSelect={() => { setForm({ ...form, timezone: "" }); setTzOpen(false); }} data-testid="profile-tz-none">
                            {t("profile.notSet")}
                          </CommandItem>
                        )}
                        {ALL_TIMEZONES.map((z) => (
                          <CommandItem
                            key={z.iana}
                            value={`${z.label} ${z.iana}`}
                            onSelect={() => { setForm({ ...form, timezone: z.iana }); setTzOpen(false); }}
                            data-testid={`profile-tz-${z.iana.replace(/[^a-z0-9]+/gi, "-")}`}
                          >
                            <Check className={`mr-2 h-4 w-4 ${form.timezone === z.iana ? "opacity-100" : "opacity-0"}`} />
                            {z.label} <span className="ml-1 font-mono text-xs text-slate-400">({z.iana}, {tzOffsetLabel(z.iana)})</span>
                          </CommandItem>
                        ))}
                      </CommandGroup>
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
              <p className="text-xs text-slate-500">{t("profile.tzNote")}</p>
            </div>
            <div className="space-y-1.5">
              <Label>{t("profile.emailL")}</Label>
              <div className="relative">
                <Input value={user?.email || ""} disabled className="pr-9 text-slate-500" data-testid="profile-email-input" />
                <Lock className="absolute right-3 top-2.5 h-4 w-4 text-slate-400" />
              </div>
              <p className="text-xs text-slate-500">{t("profile.emailNote")}</p>
            </div>
          </div>
          <Button onClick={save} disabled={saving || !form.name.trim()} className="bg-slate-900 hover:bg-slate-800" data-testid="profile-save-button">
            {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} {t("common.saveChanges")}
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}
